// Ported from packages/desk/src/mobile/ProfileSheet.tsx (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * The account: name, email and role; the host's rows (switch school or workspace, the Guide, the tour again);
 * Appearance; the way back out ("Back to GoGamo") and Sign out. The exits live here, never on the home screen.
 */
import { useId, type ReactNode } from "react";
import { ChevronGlyph } from "./glyphs";
import { Sheet } from "./Sheet";
import type { AppearanceValue, MobileAppearance, MobileProfileItem } from "./types";

const APPEARANCE: Array<{ value: AppearanceValue; label: string }> = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

export function ProfileSheet({
  open,
  name,
  email,
  role,
  initials,
  avatar,
  items,
  appearance,
  exitLabel = "Back to GoGamo",
  onExit,
  onSignOut,
  onClose,
}: {
  open: boolean;
  name: string;
  email?: string | undefined;
  role?: string | undefined;
  initials: string;
  /** A drawn picture in place of the initials (a Radar addition, to copy back to the desk). */
  avatar?: ReactNode;
  items: MobileProfileItem[];
  appearance?: MobileAppearance | undefined;
  exitLabel?: string | undefined;
  onExit?: (() => void) | undefined;
  onSignOut: () => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const run = (fn: () => void) => {
    onClose();
    fn();
  };
  return (
    <Sheet open={open} onClose={onClose} title="Profile" titleId={titleId} data="profile" className="rdr-mprofile">
      <div className="rdr-mprofile-who">
        <span className={"rdr-mprofile-avatar" + (avatar ? " art" : "")} aria-hidden="true">
          {avatar ?? initials}
        </span>
        <span className="rdr-mrow-text">
          <span className="rdr-mrow-title">{name}</span>
          {email && <span className="rdr-mrow-sub">{email}</span>}
          {role && <span className="rdr-mrow-sub">{role}</span>}
        </span>
      </div>

      {items.length > 0 && (
        <ul className="rdr-mlist">
          {items.map((it) => (
            <li key={it.id}>
              <button type="button" className="rdr-mrow" data-rdr-profile-item={it.id} onClick={() => run(it.run)}>
                {it.icon && (
                  <span className="rdr-mrow-ic" aria-hidden="true">
                    {it.icon}
                  </span>
                )}
                <span className="rdr-mrow-text">
                  <span className="rdr-mrow-title">{it.label}</span>
                  {it.detail && <span className="rdr-mrow-sub">{it.detail}</span>}
                </span>
                <span className="rdr-mrow-go" aria-hidden="true">
                  <ChevronGlyph />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {appearance && (
        <div className="rdr-mappear" role="radiogroup" aria-label="Appearance">
          <span className="rdr-mlib-label">Appearance</span>
          <div className="rdr-mseg">
            {APPEARANCE.map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={appearance.value === o.value} className={appearance.value === o.value ? "on" : ""} onClick={() => appearance.onChange(o.value)}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <ul className="rdr-mlist">
        {onExit && (
          <li>
            <button type="button" className="rdr-mrow" data-rdr-profile-item="exit" onClick={() => run(onExit)}>
              <span className="rdr-mrow-text">
                <span className="rdr-mrow-title">{exitLabel}</span>
              </span>
            </button>
          </li>
        )}
        <li>
          <button type="button" className="rdr-mrow danger" data-rdr-profile-item="sign-out" onClick={() => run(onSignOut)}>
            <span className="rdr-mrow-text">
              <span className="rdr-mrow-title">Sign out</span>
            </span>
          </button>
        </li>
      </ul>
    </Sheet>
  );
}
